/**
 * ClutchTimeRadar.js
 *
 * Simplified Exam Radar - Less is more.
 * Shows only what matters: 3 topics to focus on, skip the rest.
 *
 * Gen Z Design Principles:
 * - Reduce overwhelm (show less, not more)
 * - Clear direction (exactly what to do)
 * - Time-aware (adjust based on days until test)
 * - Confidence building (tell them when they're ready)
 *
 * December 2025
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

// Simplified topic card - just name and percentage
const FocusTopicCard = ({ topic, index }) => {
  const percentage = Math.round(topic.mastery || 0);

  return (
    <motion.div
      className="clutch-focus-card"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <span className="clutch-focus-number">{index + 1}</span>
      <span className="clutch-focus-name">{topic.name}</span>
      <span className="clutch-focus-mastery">{percentage}%</span>
    </motion.div>
  );
};

// Compact skip list - just names in a row
const SkipList = ({ topics }) => {
  if (!topics || topics.length === 0) return null;

  return (
    <div className="clutch-skip-section">
      <div className="clutch-skip-label">You already know these (skip):</div>
      <div className="clutch-skip-list">
        {topics.map((topic, idx) => (
          <span key={idx} className="clutch-skip-item">
            {topic.name} {Math.round(topic.mastery)}%
            {idx < topics.length - 1 && ' • '}
          </span>
        ))}
      </div>
    </div>
  );
};

// Time-aware header message
const TimeMessage = ({ testDate, focusCount }) => {
  if (!testDate) return null;

  const testDateObj = new Date(testDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  testDateObj.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((testDateObj - today) / (1000 * 60 * 60 * 24));

  let timeText = '';
  let subText = '';

  if (diffDays <= 0) {
    timeText = "Test is today";
    subText = focusCount > 0 ? `Quick review on ${focusCount} weak spot${focusCount > 1 ? 's' : ''}.` : "You're ready. Trust yourself.";
  } else if (diffDays === 1) {
    timeText = "Test is tomorrow";
    subText = focusCount > 0 ? `Focus only on these ${focusCount}.` : "You've got this.";
  } else if (diffDays <= 3) {
    timeText = `Test in ${diffDays} days`;
    subText = focusCount > 0 ? `Nail these ${focusCount} and you're set.` : "Looking good.";
  } else {
    timeText = `Test in ${diffDays} days`;
    subText = focusCount > 0 ? `Plenty of time. Start with the weak spots.` : "You're ahead of the game.";
  }

  return (
    <div className="clutch-time-message">
      <div className="clutch-time-main">{timeText}</div>
      <div className="clutch-time-sub">{subText}</div>
    </div>
  );
};

// Empty state - no data yet
const EmptyState = ({ subject, onTellTopics }) => {
  return (
    <div className="clutch-empty-state">
      <div className="clutch-empty-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"/>
        </svg>
      </div>
      <h3>No study history for {subject}</h3>
      <p>Tell me what topics are on the test and I'll help you focus.</p>
      <button className="clutch-action-btn primary" onClick={onTellTopics}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" x2="12" y1="19" y2="22"/>
        </svg>
        Tell me the topics
      </button>
    </div>
  );
};

// Ready state - all topics mastered
const ReadyState = ({ onQuickReview, onClose }) => {
  return (
    <div className="clutch-ready-state">
      <div className="clutch-ready-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      </div>
      <h3>You're ready.</h3>
      <p>All your topics are above 80%. Trust your preparation.</p>
      <div className="clutch-ready-actions">
        <button className="clutch-action-btn secondary" onClick={onQuickReview}>
          Quick review anyway
        </button>
        <button className="clutch-action-btn primary" onClick={onClose}>
          Got it, I'm ready
        </button>
      </div>
    </div>
  );
};

// Imported topics view (from study guide)
const ImportedTopicsView = ({ topics, testDate }) => {
  const focusTopics = topics.slice(0, 5); // Show max 5

  return (
    <div className="clutch-imported-view">
      <TimeMessage testDate={testDate} focusCount={focusTopics.length} />

      <div className="clutch-section">
        <div className="clutch-section-header focus">
          <span className="clutch-section-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#6366f1">
              <circle cx="12" cy="12" r="10"/>
            </svg>
          </span>
          <span>FROM YOUR STUDY GUIDE</span>
        </div>

        <div className="clutch-focus-list">
          {focusTopics.map((topic, idx) => (
            <motion.div
              key={idx}
              className="clutch-focus-card imported"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <span className="clutch-focus-number">{idx + 1}</span>
              <span className="clutch-focus-name">{topic.name}</span>
              <span className="clutch-focus-mastery new">New</span>
            </motion.div>
          ))}
        </div>

        {topics.length > 5 && (
          <div className="clutch-more-topics">
            +{topics.length - 5} more topics
          </div>
        )}
      </div>

      <div className="clutch-confidence-message">
        Mira will quiz you on these to find your weak spots.
      </div>
    </div>
  );
};

// Main simplified Radar
const ClutchTimeRadar = ({
  subject,
  testDate,
  importedTopics = [],
  onStartStudying,
  onClose,
  onTellTopics,
  onScreenShare,
  onInviteFriend
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [radarData, setRadarData] = useState(null);

  const hasImportedTopics = importedTopics && importedTopics.length > 0;

  // Fetch radar data
  useEffect(() => {
    const fetchRadar = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(`/api/clutch-time/radar/${encodeURIComponent(subject)}`, {
          withCredentials: true
        });

        if (response.data.success) {
          setRadarData(response.data);
        } else {
          setError(response.data.error || 'Failed to load topics');
        }
      } catch (err) {
        console.error('Error fetching radar:', err);
        setError(err.response?.data?.error || 'Failed to load topics');
      } finally {
        setLoading(false);
      }
    };

    if (subject) {
      fetchRadar();
    }
  }, [subject]);

  // Start session
  const handleStartStudying = async () => {
    try {
      const response = await axios.post('/api/clutch-time/session/start', {
        subject,
        test_date: testDate
      }, { withCredentials: true });

      if (response.data.success) {
        onStartStudying && onStartStudying(response.data.session_id, radarData);
      }
    } catch (err) {
      console.error('Error starting session:', err);
    }
  };

  // Calculate what to show
  const getSimplifiedView = () => {
    if (!radarData || !radarData.has_history) {
      return { focusTopics: [], skipTopics: [], isReady: false };
    }

    // Combine focus and review into one priority list
    const allWeakTopics = [
      ...(radarData.topics.focus || []),
      ...(radarData.topics.review || [])
    ];

    // Sort by mastery (lowest first) and take top 3
    const sortedWeak = allWeakTopics.sort((a, b) => (a.mastery || 0) - (b.mastery || 0));
    const focusTopics = sortedWeak.slice(0, 3);

    // Everything else is "skip"
    const skipTopics = [
      ...sortedWeak.slice(3),
      ...(radarData.topics.confident || [])
    ];

    // Check if ready (no weak topics)
    const isReady = focusTopics.length === 0;

    return { focusTopics, skipTopics, isReady };
  };

  const { focusTopics, skipTopics, isReady } = getSimplifiedView();

  return (
    <div className="clutch-radar-container clutch-radar-simple">
      {/* Header */}
      <div className="clutch-radar-header">
        <div className="clutch-header-left">
          <div className="clutch-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
          </div>
          <h2>{subject}</h2>
        </div>
        <button className="clutch-close-btn" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="clutch-radar-content">
        {loading && (
          <div className="clutch-loading">
            <div className="clutch-spinner" />
            <p>Checking your weak spots...</p>
          </div>
        )}

        {error && (
          <div className="clutch-error">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Try Again</button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* No history - show empty or imported */}
            {!radarData?.has_history && !hasImportedTopics && (
              <EmptyState subject={subject} onTellTopics={onTellTopics} />
            )}

            {/* Imported topics from study guide */}
            {!radarData?.has_history && hasImportedTopics && (
              <ImportedTopicsView topics={importedTopics} testDate={testDate} />
            )}

            {/* Has history - show simplified radar */}
            {radarData?.has_history && isReady && (
              <ReadyState onQuickReview={handleStartStudying} onClose={onClose} />
            )}

            {radarData?.has_history && !isReady && (
              <div className="clutch-simplified-view">
                <TimeMessage testDate={testDate} focusCount={focusTopics.length} />

                {/* Focus topics - max 3 */}
                <div className="clutch-section">
                  <div className="clutch-section-header focus">
                    <span>Focus on these {focusTopics.length}:</span>
                  </div>
                  <div className="clutch-focus-list">
                    {focusTopics.map((topic, idx) => (
                      <FocusTopicCard key={topic.id || idx} topic={topic} index={idx} />
                    ))}
                  </div>
                </div>

                {/* Skip list - compact */}
                <SkipList topics={skipTopics} />

                {/* Confidence message */}
                <motion.div
                  className="clutch-confidence-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  Nail these {focusTopics.length} and you're ready.
                </motion.div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer - Start button */}
      {!loading && !error && (radarData?.has_history || hasImportedTopics) && !isReady && (
        <div className="clutch-radar-footer">
          <button className="clutch-start-btn" onClick={handleStartStudying}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" x2="12" y1="19" y2="22"/>
            </svg>
            Let's go
          </button>
        </div>
      )}
    </div>
  );
};

export default ClutchTimeRadar;
